/**
 * Content for /financing: finance and payment options across all CRS services.
 *
 * LENDER-AGNOSTIC: no provider names in this file. Provider disclosure lives
 * only in FinanceProviderDisclosure.
 */

export const FINANCING_STANDING_DISCLAIMER =
  "Subject to status, property suitability and a credit agreement.";

/** Reusable provider-agnostic finance copy for service pages and forms. */
export const FINANCE_SPREAD_COST_COPY =
  "Depending on the work, monthly payment plans may be available so you can spread cost rather than pay in full on completion. We'll confirm what applies to your property.";

export const FINANCE_TERMS_BEFORE_COMMIT_COPY =
  "You'll receive a personalised quote with clear payment options and full terms before you decide anything.";

export const FINANCE_SERVICES_HUB_COPY =
  "Many of the measures we install can be paid for monthly, and some may qualify for funded routes. We'll confirm what applies to your property when we speak.";

export const financingLp = {
  metaTitle: "Finance & Payment Options | CRS Energy",
  metaDescription:
    "Spread the cost of insulation, heating, solar and home repairs with monthly payment plans and funded routes across Wales and the South of England. No-obligation enquiry.",

  heroH1: "Spread The Cost Of A Warmer, Cheaper-To-Run Home",
  heroSub:
    "Finance and funded options are available across the work we do, from insulation and damp remediation to heating, solar and battery storage. Tell us what you're interested in and we'll explain the options that apply to your home.",
  heroMicrocopy: FINANCING_STANDING_DISCLAIMER,
  heroTrustRow: [
    "No-Obligation Enquiry",
    "MCS & Trustmark Accredited",
    "Options For Most Services",
    "Local Team, Wales & South of England",
  ],

  howItWorks: [
    {
      step: 1,
      title: "Tell Us About Your Home",
      body: "A short enquiry form: your details, postcode and the services you're interested in.",
    },
    {
      step: 2,
      title: "Talk Through Your Options",
      body: "A member of the team explains the payment and funding routes that apply to your property - and for many services, including solar, can give you an estimate on the call. No jargon, no pressure.",
    },
    {
      step: 3,
      title: "Your Written Quote & Your Decision",
      body: "You'll receive a clear, itemised written quote. Happy to go ahead? Choose to pay in full, or apply for finance through our partners Ideal4Finance and Hometree Finance. It's always your choice and you'll have a clear written proposal in front of you before you commit to anything.",
    },
    {
      step: 4,
      title: "Survey & Final Design",
      body: "Once your order is confirmed, we finalise the details. Depending on the service, this is completed remotely - as with our solar desktop survey - or with a home visit where it's genuinely needed. We'll confirm what applies when we speak.",
    },
    {
      step: 5,
      title: "Installation, Aftercare & Support",
      body: "Work is carried out by our certified, insured installation teams, and we stay on hand afterwards, with support and guarantees explained up front.",
    },
  ],

  servicesIntro:
    "Most of the work we do can be paid for monthly, and some measures may qualify for funded routes such as government-backed schemes. Here's where each service sits. We'll confirm what applies to your home when we speak.",

  faqs: [
    {
      question: "Do I have to pay anything upfront?",
      answer:
        "No. Many customers choose finance because it allows them to spread the cost over affordable monthly payments rather than paying everything in one go. If a deposit is required, we'll make this clear before you proceed.",
    },
    {
      question: "Can I get a finance decision straight away?",
      answer:
        "In many cases, yes. Applications are often assessed quickly, although some may need a little longer if the lender requires additional checks or supporting documents.",
    },
    {
      question: "What if I'm not accepted for finance?",
      answer:
        "Not every application is approved, and finance is always subject to status. If finance isn't available, we'll discuss any alternative payment options that may be suitable for you.",
    },
    {
      question: "Do I need a written quote before I apply?",
      answer:
        "Yes. Your finance application must match your CRS Energy quotation, including the amount you're borrowing, your property address, and the description of the work. We'll provide everything you need before you apply.",
    },
    {
      question: "Can someone else complete the application for me?",
      answer:
        "No. For your protection and to comply with lending regulations, the finance application must always be completed by the person applying for the loan or lease. We're happy to contact the lender to get you the help you need.",
    },
    {
      question: "Will applying affect my credit score?",
      answer:
        "The lender will explain if a credit search is required as part of your application. Any impact depends on the type of search they carry out. If you have questions, it's best to contact the lender you are applying through.",
    },
    {
      question: "When can my installation be booked?",
      answer:
        "As soon as your finance agreement has been completed and any required deposit has been received, we'll arrange a convenient installation date with you.",
    },
    {
      question: "How long does the whole process take?",
      answer:
        "Every application is different, but once your finance has been approved and completed, we'll contact you promptly to arrange your installation at the earliest suitable date.",
    },
    {
      question: "Can I choose finance for any CRS Energy installation?",
      answer:
        "Finance is available on many of our products and services, subject to eligibility and the lender's approval. We'll confirm what's available when we prepare your quotation.",
    },
    {
      question: "Who do I contact if I need help with my application?",
      answer:
        "We're here to help for questions regarding your purchase, we can't complete the application on your behalf. If you have questions related to your finance application, it's best to speak to the lender that you are applying with.",
    },
    {
      question: "Why do customers choose finance?",
      answer:
        "Many homeowners choose finance because it allows them to improve their home now while spreading the cost over manageable monthly payments. It can make larger projects, such as solar panels, battery storage or heat pumps, much more affordable without delaying the installation.",
    },
    {
      question: "What's the difference between the loan and the Solar Lease Plan?",
      answer:
        "The Hometree Solar Plan is an all-inclusive lease, with low or no upfront cost. You get a custom-designed system installed and gain the full benefits of the generated and stored energy, bill savings and export payments. Unlike a loan, Hometree Finance take care of monitoring and maintaining the system, giving you confidence that it'll run efficiently and safely, with no unexpected repair or replacement costs for the lifetime of your plan.",
    },
  ],
} as const;

/** Service slugs in display order for the payment-routes grid. */
export const FINANCING_SERVICE_SLUGS = [
  "solar-pv",
  "air-source-heat-pumps",
  "boilers-and-heating",
  "heating-controls",
  "loft-insulation",
  "cavity-wall-insulation",
  "cavity-wall-insulation-removal",
  "internal-wall-insulation",
  "damp-and-mould-remediation",
  "remedial-wall-ties",
  "ventilation-and-extraction",
] as const;

export const FINANCING_SERVICE_LABELS: Record<(typeof FINANCING_SERVICE_SLUGS)[number], string> = {
  "solar-pv": "Solar & Battery",
  "air-source-heat-pumps": "Air Source Heat Pumps",
  "boilers-and-heating": "Boilers & Heating",
  "heating-controls": "Heating Controls",
  "loft-insulation": "Loft Insulation",
  "cavity-wall-insulation": "Cavity Wall Insulation",
  "cavity-wall-insulation-removal": "Cavity Wall Insulation Removal",
  "internal-wall-insulation": "Internal Wall Insulation",
  "damp-and-mould-remediation": "Damp & Mould Remediation",
  "remedial-wall-ties": "Remedial Wall Ties",
  "ventilation-and-extraction": "Ventilation & Extraction",
};

export const FINANCING_PAYMENT_TAGS: Record<(typeof FINANCING_SERVICE_SLUGS)[number], readonly string[]> = {
  "solar-pv": ["Monthly loan", "Solar lease available", "Ask about funding"],
  "air-source-heat-pumps": ["Monthly options", "Grant routes may apply"],
  "boilers-and-heating": ["Monthly options", "Funded routes may apply"],
  "heating-controls": ["Monthly options"],
  "loft-insulation": ["Often funded", "Monthly options"],
  "cavity-wall-insulation": ["Often funded", "Monthly options"],
  "cavity-wall-insulation-removal": ["Monthly options"],
  "internal-wall-insulation": ["Monthly options", "Ask about funding"],
  "damp-and-mould-remediation": ["Monthly options"],
  "remedial-wall-ties": ["Monthly options"],
  "ventilation-and-extraction": ["Monthly options"],
};

export const FINANCING_SERVICE_PATHS: Record<(typeof FINANCING_SERVICE_SLUGS)[number], string> = {
  "solar-pv": "/solar-pv",
  "air-source-heat-pumps": "/air-source-heat-pumps",
  "boilers-and-heating": "/boilers-heating",
  "heating-controls": "/heating-controls",
  "loft-insulation": "/loft-insulation",
  "cavity-wall-insulation": "/lp/cavity-wall-insulation",
  "cavity-wall-insulation-removal": "/lp/cavity-insulation-removal",
  "internal-wall-insulation": "/internal-wall-insulation",
  "damp-and-mould-remediation": "/damp-survey",
  "remedial-wall-ties": "/remedial-wall-ties",
  "ventilation-and-extraction": "/ventilation-extraction",
};

/** Checkbox chip options on the /financing enquiry form. */
export const FINANCING_FORM_SERVICE_OPTIONS = [
  "Solar & Battery",
  "Heat Pumps",
  "Boilers & Heating",
  "Heating Controls",
  "Loft Insulation",
  "Cavity Wall Insulation",
  "Wall Insulation (Internal)",
  "Cavity Wall Removal",
  "Damp & Mould",
  "Ventilation",
  "Wall Ties",
  "Not Sure Yet",
] as const;

export const FINANCING_FORM_HOMEOWNER_OPTIONS = ["Yes", "No", "Landlord"] as const;
